import {makeAutoObservable, observable, runInAction} from 'mobx'
import React from 'react'
import {Lang} from 'yougile-lang'
import {Time, UIUtil, Util} from 'yougile-util'
import {DataObjectId, Hub, ProjectType} from 'yougile-model'
import {Items} from '@api/model/items'
import {EventBus} from '@client/event-bus'
import {openProjectParticipants} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {FileChooser} from '@common/file-chooser'
import {
  showConfirm,
  showDeleteConfirm
} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {projectImportFromJson} from '@extensions/project-import-from-json'
import {
  ProjectData,
  projectImportToJson
} from '@extensions/project-import-to-json'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {UserAccess} from '@extensions/user-access'
import {canEdit, listForAdmin, listProjects} from '../dashboard-utils'

export interface CompanyProject {
  order: number
  hub: Hub
  users: UserProxy[]
}

interface State {
  listProjects: CompanyProject[]
  listArchived: CompanyProject[]
  listForAdmin: CompanyProject[]
  filtered: CompanyProject[]
  filterQuery: string
  maxShowCurrentProjects: number
  maxShowArchivedProjects: number
  maxShowAdminProjects: number
  projEditEdited: Hub | undefined
  projectsEditedProjectId: DataObjectId | undefined
  projectsDraggedProjectId: DataObjectId | undefined
  projEditTitle: string
  newProjIsOpened: boolean
  newProjType: ProjectType
  importFromFileIsOpened: boolean
  importFromTrelloIsOpened: boolean

  projectImportIsOpen?: boolean
  projectImportInProgress?: boolean
  projectImportProgress?: number
  projectImportName?: string
  projectImportErrors: string[]
  showPopupEditId?: DataObjectId
}

export class CompanyProjectsStore {
  state: State = {
    listProjects: [],
    listArchived: [],
    listForAdmin: [],
    filtered: [],
    filterQuery: '',
    maxShowCurrentProjects: 10,
    maxShowArchivedProjects: 10,
    maxShowAdminProjects: 10,
    projEditEdited: undefined,
    projectsEditedProjectId: undefined,
    projectsDraggedProjectId: undefined,
    projEditTitle: '',
    newProjIsOpened: false,
    newProjType: 'default',
    importFromFileIsOpened: false,
    importFromTrelloIsOpened: false,
    projectImportErrors: []
  }

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  setProjectTitleEditing(projectId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const project = box.getHub(projectId)
    if (!project) {
      return
    }

    if (!PermissionsChecker.checkRename(project)) {
      MessageBox.show(Lang.msg.permissionsForbiddenRename, 'warning')
      return
    }

    this.state.projectsEditedProjectId = projectId
  }

  setProjectIdEditing(projectId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const project = box.getHub(projectId)
    if (!project) {
      return
    }

    if (!PermissionsChecker.checkRename(project)) {
      MessageBox.show(Lang.msg.permissionsForbiddenRename, 'warning')
      return
    }

    this.state.showPopupEditId = projectId
  }

  resetProjectIdEditing(): void {
    this.state.showPopupEditId = undefined
  }

  submitProjectTitle(projectId: DataObjectId, title?: string): void {
    if (this.state.projectsEditedProjectId === projectId && title) {
      const box = Sync.getCurrentSandbox()
      const project = box.getHub(projectId)
      if (project && Items.rename(Items.fromHub(project), title)) {
        box.editTitle(project, title)
      }
    }

    this.state.projectsEditedProjectId = undefined
  }

  editProject(project: Hub): void {
    if (!PermissionsChecker.checkRename(project)) {
      MessageBox.show(Lang.msg.permissionsForbiddenRename, 'warning')
      return
    }
    this.state.projEditEdited = project
  }

  projEditSubmit(): void {
    const box = Sync.getCurrentSandbox()
    const title = this.state.projEditTitle.trim()
    if (title && this.state.projEditEdited) {
      if (!Items.rename(Items.fromHub(this.state.projEditEdited), title)) {
        this.state.projEditEdited = undefined
        return
      }
      box.editTitle(this.state.projEditEdited, title)
    }
    this.state.projEditEdited = undefined
  }

  projEditCancel(): boolean {
    this.state.projEditEdited = undefined
    return true
  }

  newCrmProjOpen(): void {
    this.state.newProjType = 'crm'
    this.state.newProjIsOpened = true
  }

  async newProjOpen(event: React.MouseEvent): Promise<void> {
    this.state.newProjType = 'default'
    if (event?.shiftKey) {
      event.preventDefault()
      if (await showConfirm(Lang.msg.companies.uploadProjectFromJson)) {
        void this.projectImportFromJson(event)
      }
      return
    }
    this.state.newProjIsOpened = true
  }

  newProjClose(): boolean {
    this.state.newProjIsOpened = false
    return true
  }

  importFromFileOpen(): void {
    this.state.importFromFileIsOpened = true
  }

  setImportFromFileIsOpened(flag: boolean): void {
    this.state.importFromFileIsOpened = flag
  }

  importFromTrelloOpen(): void {
    this.state.importFromTrelloIsOpened = true
  }

  setImportFromTrelloIsOpened(flag: boolean): void {
    this.state.importFromTrelloIsOpened = flag
  }

  calculateLists(): void {
    this.calculateListProjects()
    this.calculateListArchived()
    this.calculateListForAdmin()
  }

  private calculateListProjects(): void {
    this.state.listProjects = this.prepareProjectList(
      globals.getCompanyId(),
      listProjects()
    )
  }

  private calculateListForAdmin(): void {
    this.state.listForAdmin = this.prepareProjectList(
      globals.getCompanyId(),
      listForAdmin()
    )
  }

  private calculateListArchived(): void {
    this.state.listArchived = this.prepareProjectList(
      globals.getCompanyId(),
      listProjects({isArchived: true, showOnlyUserProjects: false})
    )
  }

  private prepareProjectList(
    companyId: DataObjectId,
    hubs: Hub[]
  ): CompanyProject[] {
    return hubs
      .map(project => {
        return {
          order: Project.projectOrder(companyId, project.id),
          hub: project,
          users: UserAccess.listUsersInProject(project)
            .map(x => UserProxy.create(x))
            .filter((x): x is UserProxy => typeof x !== 'undefined') // todo: fix
        }
      })
      .sort((a, b) => a.order - b.order)
  }

  async goToProject(
    projectId: DataObjectId,
    event: React.MouseEvent
  ): Promise<void> {
    const box = Sync.getCurrentSandbox()
    const project = box.getHub(projectId)
    if (!project) {
      return
    }

    if (
      UIUtil.isConsumed(event as unknown as Event) ||
      !!this.state.projEditEdited
    ) {
      return
    }
    if (event?.shiftKey) {
      event.preventDefault()
      if (await showConfirm(Lang.msg.companies.downloadProjectAsJson)) {
        const data = projectImportToJson(box, project)
        UIUtil.downloadAsText(project.title + '.json', JSON.stringify(data))
      }
      return
    }
    if (!Items.click(Items.fromHub(project))) {
      return
    }
    if (event.button === 1) {
      // wheel click
      const newUrl = location.href.replace(
        PageService.getPath(),
        box.calculatePath(project)
      )
      window.open(newUrl, '_blank')
    } else {
      PageService.goToProject(project)
    }
  }

  showMoreCurrentProjects(): void {
    this.state.maxShowCurrentProjects += 10
  }

  showMoreArchivedProjects(): void {
    this.state.maxShowArchivedProjects += 10
  }

  showMoreAdminProjects(): void {
    this.state.maxShowAdminProjects += 10
  }

  projectsDragStart(projectId: DataObjectId, e: React.DragEvent): void {
    const companyId = globals.getCompanyId()
    DragData.set(e, {type: 'project', id: projectId})
    this.state.projectsDraggedProjectId = projectId

    Project.fixOrderIfNeeded(companyId)
    let order = Project.getOrder(companyId)
    if (!order || order.length === 0) {
      order = this.state.listProjects.map(p => p.hub.id)
      Project.setOrder(companyId, order)
    } else {
      this.state.listProjects.forEach(p => {
        if (!order.includes(p.hub.id)) {
          order.push(p.hub.id)
        }
      })
    }
  }

  projectsDragEnd(): void {
    this.state.projectsDraggedProjectId = undefined
  }

  projectsDragEnter(projectId: DataObjectId): void {
    const companyId = globals.getCompanyId()
    const draggedProjectId = this.state.projectsDraggedProjectId
    if (!draggedProjectId || projectId === draggedProjectId) {
      return
    }
    const order = Project.getOrder(companyId)
    const ixDrag = order.indexOf(draggedProjectId)
    const ixOver = order.indexOf(projectId)
    order.splice(ixDrag, 1)
    order.splice(ixOver, 0, draggedProjectId)
    Project.setOrder(companyId, order)
    this.calculateLists()
  }

  projectsToggleArchive(project: Hub): void {
    project.complete = project.complete ? undefined : Time.now()
    project.commit()
    this.calculateLists()
  }

  projectParticipantsOpen(
    projectId: DataObjectId,
    e: React.SyntheticEvent
  ): void {
    e.stopPropagation()

    const box = Sync.getCurrentSandbox()
    const project = box.getHub(projectId)
    if (!project) {
      return
    }

    if (canEdit(project)) {
      EventBus.dispatch(openProjectParticipants(project))
    }
  }

  async duplicateProject(project: Hub): Promise<void> {
    if (!(await showConfirm(Lang.msg.confirmDuplicateProject))) {
      return
    }
    Project.duplicate(project)
  }

  async deleteProject(project: Hub): Promise<void> {
    if (!Items.delete(Items.fromHub(project))) {
      return
    }
    if (!PermissionsChecker.checkDeletion(project)) {
      MessageBox.show(Lang.msg.permissionsForbiddenProjectDelete, 'warning')
    } else if (
      await showDeleteConfirm({
        title: Lang.msg.confirmDeleteProject,
        subtitle: Lang.msg.confirmDeleteProjectHint
      })
    ) {
      Project.remove(project)
      this.calculateLists()
    }
  }

  setProjEditTitle(value: string): void {
    this.state.projEditTitle = Util.removeLeadingString(value, '.')
  }

  async projectImportFromJson(event: React.MouseEvent): Promise<void> {
    const file = await FileChooser.chooseFile(event, false)
    this.state.projectImportIsOpen = true
    this.state.projectImportInProgress = true
    try {
      const dataStr = await FileChooser.readFile(file)
      const data = JSON.parse(dataStr) as ProjectData
      this.state.projectImportName = data.title
      await projectImportFromJson({
        data,
        setProgress: progress =>
          runInAction(
            () => (this.state.projectImportProgress = Math.round(progress))
          ),
        isActive: () => !!this.state.projectImportIsOpen
      })
      this.state.projectImportInProgress = false
    } catch (e) {
      this.state.projectImportInProgress = false
      this.state.projectImportErrors = [(e as Error).message]
    }
  }

  projectImportClose(): void {
    this.state.projectImportIsOpen = false
    this.state.projectImportInProgress = false
    this.state.projectImportProgress = 0
    this.state.projectImportName = undefined
    this.state.projectImportErrors = []
  }
}

const [CompanyProjectsContext, useCompanyProjectsStore] =
  createUseStore(CompanyProjectsStore)
export {CompanyProjectsContext, useCompanyProjectsStore}
